import { create } from "zustand"

import type { ProductInventoryLotsSearchParams } from "../schemas"

import {
  GetProductInventoryByLotDocument,
  type GetProductInventoryByLotQuery,
  type InventoryByLotItem,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

type ProductInventoryLotsFilters = ProductInventoryLotsSearchParams & { productId: number }

const deduplicateInventories = (
  newData: InventoryByLotItem[],
  existingData: InventoryByLotItem[]
): InventoryByLotItem[] => {
  const seen = new Set(existingData.map((item) => `${item.lotNumber}-${item.warehouseId}`))
  return [...existingData, ...newData.filter((item) => !seen.has(`${item.lotNumber}-${item.warehouseId}`))]
}

export const useProductInventoryLotsSearchStore = create(
  createSearchStore<InventoryByLotItem, ProductInventoryLotsFilters, GetProductInventoryByLotQuery>({
    documentNode: GetProductInventoryByLotDocument,
    deduplicateData: deduplicateInventories,
    getQueryInput: (filters: ProductInventoryLotsFilters, offset: number, limit: number) => {
      const { field, order, productId, warehouseAreaId } = filters

      return {
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
        filter: {
          productId,
          warehouseId: warehouseAreaId,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.inventory.listByLot.__typename,
      data:
        data.inventory.listByLot.__typename === "InventoryByLotListSuccess"
          ? (data.inventory.listByLot.items as InventoryByLotItem[])
          : [],
      error:
        data.inventory.listByLot.__typename === "InventoryByLotListFailure"
          ? data.inventory.listByLot.error
          : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
